import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { BlockingLoaderService } from '../../services/blocking-loader.service';

/**
 * Overlay de carga BLOQUEANTE a pantalla completa. Montado una única vez en el root
 * (`app.html`), cubre toda la web —incluido el header/menú— mientras un flujo largo
 * e irreversible está en curso (lo gobierna `BlockingLoaderService`).
 *
 * Es un bloqueador TRANSACCIONAL, no un pop-up promocional: por eso bloquea, a
 * diferencia del anti-patrón de pop-ups de Perrón. No se cierra con Escape ni con
 * clic en el backdrop, y atrapa el foco para que el teclado no escape al fondo. El
 * patrón de focus-trap, guardas SSR y bloqueo de scroll sigue a `blocking-notice-modal`.
 */
@Component({
  selector: 'app-processing-overlay',
  templateUrl: './processing-overlay.component.html',
  styleUrl: './processing-overlay.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessingOverlayComponent {
  private readonly loader = inject(BlockingLoaderService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly visible = this.loader.visible;
  protected readonly message = this.loader.message;

  private readonly dialogRef = viewChild<ElementRef<HTMLElement>>('dialog');
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor() {
    // Guardar/restaurar el foco al mostrar/ocultar (patrón blocking-notice-modal).
    effect(() => {
      const isVisible = this.visible();
      if (!isPlatformBrowser(this.platformId)) return;

      if (isVisible) {
        this.previouslyFocusedElement =
          (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => this.dialogRef()?.nativeElement.focus());
      } else if (this.previouslyFocusedElement) {
        const el = this.previouslyFocusedElement;
        this.previouslyFocusedElement = null;
        queueMicrotask(() => {
          if (document.contains(el)) el.focus({ preventScroll: true });
        });
      }
    });

    // Foco atrapado: el overlay no tiene elementos interactivos, así que mientras
    // está visible tragamos Escape y Tab para que el teclado no alcance el fondo.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.visible()) return;
          if (e.key === 'Escape' || e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
          }
        });
    });
  }
}
